import React, { useState } from 'react';
import {
  Container,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  Grid,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../contexts/AuthContext';
import { formatDisplayDate } from '../utils/dateDisplay';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  author: string;
  tags: string[];
  category: string;
  keywords?: string;
  canonicalUrl?: string;
  isPublished: boolean;
  createdAt: string;
}

interface BlogAdminDashboardProps {
  isEmbedded?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const AdminDashboard: React.FC<BlogAdminDashboardProps> = ({ 
  isEmbedded = false
}) => {
  const { state: authState } = useAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('Kundlistar Studio Editorial Team');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('astrology');
  const [keywords, setKeywords] = useState('');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);

  // Refs
  const contentTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  // Auto-fetch posts when component mounts and user is authenticated
  React.useEffect(() => {
    if (authState.isAuthenticated && authState.token) {
      fetchAllPosts();
    }
  }, [authState.isAuthenticated, authState.token]);

  // Fetch all posts (admin view)
  const fetchAllPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE_URL}/blog/admin/all`, {
        headers: {
          'Authorization': `Bearer ${authState.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch posts');
      }

      const data = await response.json();
      setPosts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching posts');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from title
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (!editingPostId) {
      // Only auto-generate for new posts
      setSlug(
        newTitle
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '')
      );
    }
  };

  // Create or update post
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (!title || !slug || !description || !content) {
        setError('Title, slug, description, and content are required');
        return;
      }

      const postData = {
        title,
        slug: slug.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, ''),
        description,
        content,
        author,
        tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
        category,
        keywords,
        canonicalUrl,
        isPublished
      };

      const url = editingPostId ? `${API_BASE_URL}/blog/${editingPostId}` : `${API_BASE_URL}/blog`;
      const method = editingPostId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authState.token}`
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editingPostId ? 'update' : 'create'} post`);
      }

      setSuccess(`Post ${editingPostId ? 'updated' : 'created'} successfully!`);
      
      // Reset form
      setTitle('');
      setSlug('');
      setDescription('');
      setContent('');
      setAuthor('Kundlistar Studio Editorial Team');
      setTags('');
      setCategory('astrology');
      setKeywords('');
      setCanonicalUrl('');
      setIsPublished(true);
      setEditingPostId(null);

      // Refresh posts list
      fetchAllPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving post');
    } finally {
      setLoading(false);
    }
  };

  // Edit post
  const handleEditPost = (post: BlogPost) => {
    setTitle(post.title);
    setSlug(post.slug);
    setDescription(post.description);
    setContent(post.content);
    setAuthor(post.author);
    setTags(post.tags.join(', '));
    setCategory(post.category);
    setKeywords(post.keywords || '');
    setCanonicalUrl(post.canonicalUrl || '');
    setIsPublished(post.isPublished);
    setEditingPostId(post._id);
    window.scrollTo(0, 0);
  };

  // Delete post
  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/blog/${postToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authState.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      setSuccess('Post deleted successfully!');
      setShowDeleteDialog(false);
      setPostToDelete(null);
      fetchAllPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting post');
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setTitle('');
    setSlug('');
    setDescription('');
    setContent('');
    setAuthor('Kundlistar Studio Editorial Team');
    setTags('');
    setCategory('astrology');
    setKeywords('');
    setCanonicalUrl('');
    setIsPublished(true);
    setEditingPostId(null);
  };

  const formatDate = (dateString: string) => {
    return formatDisplayDate(dateString);
  };

  const insertMarkdown = (before: string, after: string, placeholder: string) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end) || placeholder;
    const newContent = content.substring(0, start) + before + selectedText + after + content.substring(end);
    
    setContent(newContent);
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Check if user is authenticated and is admin
  if (!authState.isAuthenticated || !authState.token) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h6">
          Not Authenticated
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please login first to access blog administration
        </Typography>
      </Box>
    );
  }

  if (authState.user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h6">
          Access Denied
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You need admin privileges to manage blogs
        </Typography>
      </Box>
    );
  }

  return isEmbedded ? (
    <Box sx={{ mb: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Blog Admin Form Content - Embedded */}
          <Paper sx={{ p: 4, mb: 3, boxShadow: 2 }}>
            <Typography variant="h5" sx={{ mb: 3, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
              Create New Blog Post
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Post Title"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter blog post title"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="url-slug-here"
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Meta Description (SEO)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description for search engines (160 characters)"
              maxRows={3}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
              Blog Content (Markdown)
            </Typography>
            <Box sx={{ mb: 3, bgcolor: "var(--app-surface-neutral, #fff)", border: "1px solid var(--app-border, #ddd)", borderRadius: 1 }}>
              <Box sx={{ p: 1, borderBottom: "1px solid var(--app-border, #ddd)", display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => insertMarkdown('**', '**', 'bold text')}>Bold</Button>
                <Button size="small" onClick={() => insertMarkdown('_', '_', 'italic')}>Italic</Button>
                <Button size="small" onClick={() => insertMarkdown('# ', '\n', 'Heading')}>H1</Button>
                <Button size="small" onClick={() => insertMarkdown('## ', '\n', 'Heading')}>H2</Button>
                <Button size="small" onClick={() => insertMarkdown('- ', '\n', 'item')}>List</Button>
                <Button size="small" onClick={() => insertMarkdown('`', '`', 'code')}>Code</Button>
                <Button size="small" onClick={() => insertMarkdown('[', '](url)', 'Link')}>Link</Button>
              </Box>
              <textarea
                ref={contentTextareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write in Markdown..."
                style={{
                  width: '100%',
                  height: '400px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Author"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <MenuItem value="astrology">Astrology</MenuItem>
                    <MenuItem value="vedic">Vedic Astrology</MenuItem>
                    <MenuItem value="kp">KP Astrology</MenuItem>
                    <MenuItem value="nadi">Nadi Astrology</MenuItem>
                    <MenuItem value="bnn">BNN Astrology</MenuItem>
                    <MenuItem value="jaimini">Jaimini Astrology</MenuItem>
                    <MenuItem value="western">Western Astrology</MenuItem>
                    <MenuItem value="predictions">Predictions</MenuItem>
                    <MenuItem value="numerology">Numerology</MenuItem>
                    <MenuItem value="panchang">Panchang</MenuItem>
                    <MenuItem value="cms">Practice Management</MenuItem>
                    <MenuItem value="remedies">Remedies</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="SEO Keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="Comma-separated keywords for SEO"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Canonical URL (Optional)"
                  value={canonicalUrl}
                  onChange={(e) => setCanonicalUrl(e.target.value)}
                  placeholder="https://kundlistarstudio.com/blog/post-slug"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Publish Status</InputLabel>
                <Select
                  value={isPublished ? 'published' : 'draft'}
                  label="Publish Status"
                  onChange={(e) => setIsPublished(e.target.value === 'published')}
                >
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ backgroundColor: '#2e7d52', px: 4 }}
              >
                {loading ? <CircularProgress size={24} /> : editingPostId ? 'Update Post' : 'Publish Post'}
              </Button>
              {editingPostId && (
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>

          {/* Posts Table - Embedded */}
          <Typography variant="h6" sx={{ mb: 2, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
            All Blog Posts
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#2e7d52' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {post.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {post.slug}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{post.category}</TableCell>
                    <TableCell>
                      <Chip
                        label={post.isPublished ? 'Published' : 'Draft'}
                        color={post.isPublished ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(post.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditPost(post)}
                        sx={{ color: "var(--app-ink-green, #2e7d52)" }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPostToDelete(post._id);
                          setShowDeleteDialog(true);
                        }}
                        sx={{ color: "var(--app-ink-red, #d32f2f)" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        sx={{ color: "var(--app-ink-neutral, #666)" }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
            <DialogTitle>Delete Blog Post</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this blog post? This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button
                onClick={handleDeletePost}
                variant="contained"
                sx={{ backgroundColor: '#d32f2f' }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  ) : (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h3" sx={{ mb: 4, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
        Blog Admin Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Create/Edit Post Form */}
      <Paper sx={{ p: 4, mb: 6, boxShadow: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
          {editingPostId ? 'Edit Blog Post' : 'Create New Blog Post'}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Post Title"
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter blog post title"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="URL Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="url-slug-here"
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Meta Description (SEO)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short description for search engines (160 characters)"
          maxRows={3}
          sx={{ mb: 3 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
          Blog Content (Markdown)
        </Typography>
        <Box sx={{ mb: 3, bgcolor: "var(--app-surface-neutral, #fff)", border: "1px solid var(--app-border, #ddd)", borderRadius: 1 }}>
          <Box sx={{ p: 1, borderBottom: "1px solid var(--app-border, #ddd)", display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => insertMarkdown('**', '**', 'bold text')}>Bold</Button>
            <Button size="small" onClick={() => insertMarkdown('_', '_', 'italic')}>Italic</Button>
            <Button size="small" onClick={() => insertMarkdown('# ', '\n', 'Heading')}>H1</Button>
            <Button size="small" onClick={() => insertMarkdown('## ', '\n', 'Heading')}>H2</Button>
            <Button size="small" onClick={() => insertMarkdown('- ', '\n', 'item')}>List</Button>
            <Button size="small" onClick={() => insertMarkdown('`', '`', 'code')}>Code</Button>
            <Button size="small" onClick={() => insertMarkdown('[', '](url)', 'Link')}>Link</Button>
          </Box>
          <textarea
            ref={contentTextareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write in Markdown..."
            style={{
              width: '100%',
              height: '400px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '14px',
              border: 'none',
              outline: 'none',
              resize: 'vertical'
            }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="astrology">Astrology</MenuItem>
                <MenuItem value="vedic">Vedic Astrology</MenuItem>
                <MenuItem value="kp">KP Astrology</MenuItem>
                <MenuItem value="nadi">Nadi Astrology</MenuItem>
                <MenuItem value="bnn">BNN Astrology</MenuItem>
                <MenuItem value="jaimini">Jaimini Astrology</MenuItem>
                <MenuItem value="western">Western Astrology</MenuItem>
                <MenuItem value="predictions">Predictions</MenuItem>
                <MenuItem value="numerology">Numerology</MenuItem>
                <MenuItem value="panchang">Panchang</MenuItem>
                <MenuItem value="cms">Practice Management</MenuItem>
                <MenuItem value="remedies">Remedies</MenuItem>
                <MenuItem value="general">General</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="tag1, tag2, tag3"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="SEO Keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Comma-separated keywords for SEO"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Canonical URL (Optional)"
              value={canonicalUrl}
              onChange={(e) => setCanonicalUrl(e.target.value)}
              placeholder="https://kundlistarstudio.com/blog/post-slug"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Publish Status</InputLabel>
            <Select
              value={isPublished ? 'published' : 'draft'}
              label="Publish Status"
              onChange={(e) => setIsPublished(e.target.value === 'published')}
            >
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ backgroundColor: '#2e7d52', px: 4 }}
          >
            {loading ? <CircularProgress size={24} /> : editingPostId ? 'Update Post' : 'Publish Post'}
          </Button>
          {editingPostId && (
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Paper>

      {/* Posts Table */}
      <Typography variant="h5" sx={{ mb: 3, color: "var(--app-ink-green, #2e7d52)", fontWeight: 600 }}>
        All Blog Posts
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#2e7d52' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post._id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {post.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {post.slug}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{post.category}</TableCell>
                  <TableCell>
                    <Chip
                      label={post.isPublished ? 'Published' : 'Draft'}
                      color={post.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(post.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditPost(post)}
                      sx={{ color: "var(--app-ink-green, #2e7d52)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPostToDelete(post._id);
                        setShowDeleteDialog(true);
                      }}
                      sx={{ color: "var(--app-ink-red, #d32f2f)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      sx={{ color: "var(--app-ink-neutral, #666)" }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Blog Post</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this blog post? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeletePost}
            variant="contained"
            sx={{ backgroundColor: '#d32f2f' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
